// サウンドマネージャ — Web Audio API ベース。
// /sounds/ 配下の wav を pre-decode して低レイテンシで再生する。

import type { SpinSoundKey, WinSoundKey } from './types';

export type EffectSoundKey = 'confirm' | 'expect' | 'ng';
type AnySoundKey = SpinSoundKey | WinSoundKey | EffectSoundKey;

const SPIN_FILES: Record<Exclude<SpinSoundKey, 'mute'>, string> = {
  snap: 'tick_snap.wav',
  click: 'tick_click.wav',
  drum: 'tick_drum.wav',
  coin: 'tick_coin.wav',
  soft: 'tick_soft.wav',
};

/** 各演出音は 5 バリエーション (1〜5) を持つ。
 *  variant 未指定/0 = ランダム抽選、1〜5 = 該当バリエーションを再生 */
export const EFFECT_VARIANT_COUNT = 5;
const EFFECT_FILES: Record<EffectSoundKey, string[]> = {
  confirm: Array.from({ length: EFFECT_VARIANT_COUNT }, (_, i) => `effect_confirm_${i + 1}.wav`),
  expect:  Array.from({ length: EFFECT_VARIANT_COUNT }, (_, i) => `effect_expect_${i + 1}.wav`),
  ng:      Array.from({ length: EFFECT_VARIANT_COUNT }, (_, i) => `effect_ng_${i + 1}.wav`),
};

const WIN_FILES: Record<Exclude<WinSoundKey, 'mute'>, string> = {
  bell: 'win_bell.wav',
  fanfare: 'win_fanfare.wav',
  casino: 'win_casino.wav',
  chime: 'win_chime.wav',
  victory: 'win_victory.wav',
};

/** スマホで 100% にしてもうるさすぎないよう、設定 100% を実ゲイン MAX_GAIN にマッピングする。 */
const MAX_GAIN = 0.5;

class SoundManager {
  private ctx: AudioContext | null = null;
  private gain: GainNode | null = null;
  private buffers = new Map<string, AudioBuffer>();
  private loading = new Map<string, Promise<AudioBuffer>>();
  private volume = MAX_GAIN * 0.5; // 50% を初期値
  private baseUrl = '';

  /** 初回ユーザー操作後に呼ぶ（autoplay ポリシー対策）。 */
  init(baseUrl: string): void {
    if (this.ctx) return;
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    this.ctx = new Ctx();
    this.gain = this.ctx.createGain();
    this.gain.gain.value = this.volume;
    this.gain.connect(this.ctx.destination);
    this.baseUrl = baseUrl.endsWith('/') ? baseUrl : baseUrl + '/';
  }

  /** 音量設定 (0..100)。スマホ向けに 100% でも MAX_GAIN まで。 */
  setVolume(vol: number): void {
    const norm = Math.max(0, Math.min(1, vol / 100));
    this.volume = norm * MAX_GAIN;
    if (this.gain) this.gain.gain.value = this.volume;
  }

  /** wav ファイルを fetch + decode してキャッシュ */
  private async load(file: string): Promise<AudioBuffer | null> {
    if (!this.ctx) return null;
    const cached = this.buffers.get(file);
    if (cached) return cached;
    const inflight = this.loading.get(file);
    if (inflight) return inflight;

    const promise = (async () => {
      const res = await fetch(this.baseUrl + 'sounds/' + file);
      const arr = await res.arrayBuffer();
      const buf = await this.ctx!.decodeAudioData(arr);
      this.buffers.set(file, buf);
      this.loading.delete(file);
      return buf;
    })();
    this.loading.set(file, promise);
    return promise;
  }

  /** 全音源を pre-load (アイドル時推奨) */
  async preloadAll(): Promise<void> {
    if (!this.ctx) return;
    const files = [
      ...Object.values(SPIN_FILES),
      ...Object.values(WIN_FILES),
      ...Object.values(EFFECT_FILES).flat(),
    ];
    await Promise.all(files.map((f) => this.load(f).catch(() => null)));
  }

  /** スピン音 (1 tick) を即時再生。多重再生 OK。 */
  playSpin(key: SpinSoundKey, gain = 1): void {
    if (key === 'mute' || !this.ctx) return;
    const file = SPIN_FILES[key];
    void this.playFile(file, gain);
  }

  /** 特殊演出音を再生。variant: 1〜5 (省略/0 はランダム) */
  playEffect(key: EffectSoundKey, variant?: number, gain = 1): void {
    const files = EFFECT_FILES[key];
    if (!files || files.length === 0) return;
    let idx: number;
    if (variant && variant >= 1 && variant <= files.length) {
      idx = variant - 1;
    } else {
      idx = Math.floor(Math.random() * files.length);
    }
    void this.playFile(files[idx], gain);
  }

  /** 当選音を再生。 */
  playWin(key: WinSoundKey, gain = 1): void {
    if (key === 'mute' || !this.ctx) return;
    const file = WIN_FILES[key];
    void this.playFile(file, gain);
  }

  /** 任意のキーを再生（試聴用） */
  preview(key: AnySoundKey): void {
    if (key === 'mute') return;
    if (key in SPIN_FILES) {
      this.playSpin(key as SpinSoundKey);
    } else if (key in WIN_FILES) {
      this.playWin(key as WinSoundKey);
    }
  }

  private async playFile(file: string, gain: number): Promise<void> {
    if (!this.ctx || !this.gain) return;
    const buf = await this.load(file);
    if (!buf || !this.ctx) return;
    // suspended の場合はレジューム
    if (this.ctx.state === 'suspended') {
      try { await this.ctx.resume(); } catch { /* ignore */ }
    }
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    if (gain !== 1) {
      const g = this.ctx.createGain();
      g.gain.value = gain;
      src.connect(g).connect(this.gain);
    } else {
      src.connect(this.gain);
    }
    src.start();
  }

  /** AudioContext を解放 */
  dispose(): void {
    this.gain?.disconnect();
    void this.ctx?.close();
    this.ctx = null;
    this.gain = null;
    this.buffers.clear();
    this.loading.clear();
  }
}

export const soundManager = new SoundManager();
